import React, { useState, useRef, useEffect } from 'react';
import { slideApiService } from '../services/slideApiService';
import { MarketplaceEvalResponse, RateLimitError, CreateSlideWithImagesRequest } from '../types';
import MPEResults from './MPEResults';
import RateLimitModal from './RateLimitModal';

interface MarketplaceEvalTabProps {
  shopId: string;
  onSaveAndEdit?: (slideId: string) => void;
}

const MarketplaceEvalTab: React.FC<MarketplaceEvalTabProps> = ({ shopId, onSaveAndEdit }) => {
  const [capturedImages, setCapturedImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [mpeResults, setMPEResults] = useState<MarketplaceEvalResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showSaveMenu, setShowSaveMenu] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const saveMenuRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageUrlsRef = useRef<string[]>([]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Keep ref in sync with imageUrls state
  useEffect(() => {
    imageUrlsRef.current = imageUrls;
  }, [imageUrls]);

  // Cleanup object URLs only on unmount
  useEffect(() => {
    return () => {
      imageUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup long press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  // Close save menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(event.target as Node)) {
        setShowSaveMenu(false);
      }
    };

    if (showSaveMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveMenu]);

  // Attach stream to video element when camera becomes active
  useEffect(() => {
    if (isCameraActive && stream) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          const videoElement = videoRef.current;
          videoElement.srcObject = stream;
          videoElement.play().catch(err => {
            console.error('Failed to play video:', err);
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCameraActive, stream]);

  // Long-press handlers for Save as Slide button
  const handleSaveButtonPress = () => {
    if (isSaving) return;

    // Start timer for long press (400ms)
    longPressTimerRef.current = setTimeout(() => {
      setShowSaveMenu(true);
    }, 400);
  };

  const handleSaveButtonRelease = () => {
    if (isSaving) return;

    // If timer is still running, it was a short press
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;

      // Only do regular save if menu isn't showing
      if (!showSaveMenu) {
        saveAsSlide(false);
      }
    }
  };

  const startCamera = async () => {
    try {
      setError(null);
      let mediaStream: MediaStream | null = null;

      // Try environment-facing camera first (mobile rear camera)
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        });
      } catch (mobileErr) {
        // Fallback to any available camera
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }

      if (!mediaStream) {
        throw new Error('Failed to acquire camera stream');
      }

      setStream(mediaStream);
      setIsCameraActive(true);
    } catch (err) {
      setError('Failed to access camera. Please check permissions or try "Choose from Photos".');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    if (capturedImages.length >= 10) {
      setError('Maximum 10 photos allowed. Please remove some photos before capturing more.');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);

        setCapturedImages(prev => [...prev, file]);
        setImageUrls(prev => [...prev, url]);

        // Auto-select first image if none selected
        if (selectedImageIndex === null && capturedImages.length === 0) {
          setSelectedImageIndex(0);
        }
      }
    }, 'image/jpeg', 0.95);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = 10 - capturedImages.length;
    if (files.length > remainingSlots) {
      setError(`Only ${remainingSlots} more photo(s) allowed. Maximum is 10 total.`);
    }

    const filesToAdd = Array.from(files).slice(0, remainingSlots);
    const newFiles: File[] = [];
    const newUrls: string[] = [];

    filesToAdd.forEach(file => {
      newFiles.push(file);
      newUrls.push(URL.createObjectURL(file));
    });

    setCapturedImages(prev => [...prev, ...newFiles]);
    setImageUrls(prev => [...prev, ...newUrls]);

    // Auto-select first image if none selected
    if (selectedImageIndex === null && capturedImages.length === 0 && newFiles.length > 0) {
      setSelectedImageIndex(0);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    URL.revokeObjectURL(imageUrls[index]);

    setCapturedImages(prev => prev.filter((_, i) => i !== index));
    setImageUrls(prev => prev.filter((_, i) => i !== index));

    // Adjust selected image index if necessary
    if (selectedImageIndex !== null) {
      if (selectedImageIndex === index) {
        // If removing the selected image, select the first remaining image (if any)
        setSelectedImageIndex(capturedImages.length > 1 ? 0 : null);
      } else if (selectedImageIndex > index) {
        // Adjust index if we removed an earlier image
        setSelectedImageIndex(selectedImageIndex - 1);
      }
    }
  };

  const runMarketplaceEval = async () => {
    if (capturedImages.length === 0) {
      setError('Please capture or select at least one image');
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const response = await slideApiService.runStandaloneMarketplaceEval(
        shopId,
        capturedImages,
        searchQuery.trim() || undefined,
        selectedImageIndex !== null ? selectedImageIndex : undefined
      );

      setMPEResults(response);
    } catch (err) {
      console.error('Marketplace Eval error:', err);

      // Handle rate limit errors specially
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
        setError(null);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to run Marketplace Eval. Please try again.';
        setError(errorMessage);
        setRateLimitError(null);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleReRun = () => {
    setMPEResults(null);
    setError(null);
    setRateLimitError(null);
  };

  const handleClearAll = () => {
    // Revoke all URLs
    imageUrls.forEach(url => URL.revokeObjectURL(url));

    setCapturedImages([]);
    setImageUrls([]);
    setSelectedImageIndex(null);
    setSearchQuery('');
    setError(null);
    setRateLimitError(null);
    setMPEResults(null);
    setSaveSuccess(null);
    stopCamera();
  };

  // Save captured images as a new Slide
  const saveAsSlide = async (openEditModal: boolean = false) => {
    if (capturedImages.length === 0) {
      setError('No images to save');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveSuccess(null);
    setShowSaveMenu(false); // Close menu

    try {
      // Use search query as slide name, or default to "Untitled Slide"
      const slideName = searchQuery?.trim() || 'Untitled Slide';

      // Build request using existing API structure
      const request: CreateSlideWithImagesRequest = {
        slide: {
          name: slideName,
          description: '',
          categories: []
        },
        primaryImage: capturedImages[0]
      };

      // Add additional images if more than one
      if (capturedImages.length > 1) {
        request.additionalImages = capturedImages.slice(1);
      }

      const newSlide = await slideApiService.createSlideWithImages(request, shopId);

      if (openEditModal && onSaveAndEdit) {
        // Navigate to Slides tab and open edit modal
        onSaveAndEdit(newSlide.id);
      } else {
        // Show success message
        setSaveSuccess(`Slide "${newSlide.name}" created successfully`);
      }
    } catch (err) {
      console.error('Save as slide error:', err);

      if (err instanceof RateLimitError) {
        setRateLimitError(err);
        setError(null);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create slide. Please try again.';
        setError(errorMessage);
        setRateLimitError(null);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // If showing results, display MPEResults component with re-run option
  if (mpeResults) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Marketplace Eval Results
          </h2>
          <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
            <button
              onClick={handleReRun}
              style={{
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap'
              }}
            >
              🔄 Re-run with Different Image
            </button>
            <button
              onClick={handleClearAll}
              style={{
                backgroundColor: 'var(--border-light)',
                color: 'var(--text-primary)',
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap'
              }}
            >
              ✕ Start Over
            </button>
          </div>
        </div>

        <MPEResults response={mpeResults} onClose={handleReRun} />

        {/* Save as Slide and Discard Buttons */}
        {capturedImages.length > 0 && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', position: 'relative' }}>
            <div style={{ flex: 1, position: 'relative' }} ref={saveMenuRef}>
              <button
                onMouseDown={handleSaveButtonPress}
                onMouseUp={handleSaveButtonRelease}
                onMouseLeave={handleSaveButtonRelease}
                onTouchStart={handleSaveButtonPress}
                onTouchEnd={handleSaveButtonRelease}
                disabled={isSaving}
                title="Click to Save, or Hold for Options"
                style={{
                  width: '100%',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--text-inverse)',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.6 : 1,
                  transition: 'background-color 0.2s'
                }}
              >
                {isSaving ? 'Saving...' : '✅ Save as Slide'}
              </button>

              {/* Context Menu */}
              {showSaveMenu && !isSaving && (
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  marginBottom: '8px',
                  backgroundColor: 'var(--bg-card)',
                  border: '2px solid var(--primary)',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  overflow: 'hidden',
                  zIndex: 1000
                }}>
                  <button
                    onClick={() => saveAsSlide(false)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      backgroundColor: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderBottom: '1px solid var(--border-light)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <span>✅</span>
                    <span>Save as Slide</span>
                  </button>
                  <button
                    onClick={() => saveAsSlide(true)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      backgroundColor: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <span>✏️</span>
                    <span>Save as Slide and Edit</span>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={handleClearAll}
              disabled={isSaving}
              style={{
                flex: 1,
                backgroundColor: 'var(--border-light)',
                color: 'var(--text-primary)',
                padding: '12px 24px',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.6 : 1
              }}
            >
              🗑️ Discard
            </button>
          </div>
        )}

        {/* Success message */}
        {saveSuccess && (
          <div style={{
            backgroundColor: 'var(--success-bg)',
            border: '1px solid var(--success)',
            borderRadius: '6px',
            padding: '12px',
            marginTop: '12px',
            color: 'var(--success-dark)',
            fontSize: '0.875rem'
          }}>
            ✓ {saveSuccess}
          </div>
        )}

        {/* Slide name preview */}
        {capturedImages.length > 0 && (
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            marginTop: '12px',
            fontStyle: 'italic'
          }}>
            {searchQuery?.trim()
              ? `Slide will be named "${searchQuery.trim()}"`
              : 'Slide will be named "Untitled Slide"'}
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Full-screen camera overlay for mobile */}
      {isCameraActive && isMobile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'var(--photo-backdrop)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header with photo counter and Close button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            zIndex: 10
          }}>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              {capturedImages.length} photos
            </div>
            <button
              onClick={stopCamera}
              type="button"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>

          {/* Camera viewport */}
          <div style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Capture button */}
            <button
              type="button"
              onClick={capturePhoto}
              disabled={capturedImages.length >= 10}
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                border: '4px solid white',
                borderRadius: '50%',
                width: '80px',
                height: '80px',
                fontSize: '1.125rem',
                fontWeight: 600,
                cursor: capturedImages.length >= 10 ? 'not-allowed' : 'pointer',
                opacity: capturedImages.length >= 10 ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                zIndex: 10
              }}
            >
              📷
            </button>
          </div>
        </div>
      )}

      <div>
        {error && (
          <div style={{
            backgroundColor: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: '4px',
            padding: '12px',
            margin: '20px 20px 16px 20px',
            color: 'var(--error-text)',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        {/* Rate Limit Modal */}
        {rateLimitError && (
          <RateLimitModal
            error={rateLimitError}
            onClose={() => setRateLimitError(null)}
          />
        )}

        {/* Camera/Photos buttons - only show if camera not active */}
        {!isCameraActive ? (
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '8px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
              Marketplace Eval
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '20px' }}>
              Search marketplaces
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gap: '8px',
              alignItems: 'center'
            }}>
              <button
                onClick={startCamera}
                disabled={capturedImages.length >= 10}
                style={{
                  backgroundColor: 'var(--primary)',
                  color: 'var(--text-inverse)',
                  padding: '10px 8px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: capturedImages.length >= 10 ? 'not-allowed' : 'pointer',
                  opacity: capturedImages.length >= 10 ? 0.6 : 1,
                  fontSize: '0.8rem',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                Camera
              </button>
              <div style={{
                color: 'var(--text-secondary)',
                fontSize: '0.75rem',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}>OR</div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={capturedImages.length >= 10}
                style={{
                  backgroundColor: 'var(--border-light)',
                  color: 'var(--text-primary)',
                  padding: '10px 8px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: capturedImages.length >= 10 ? 'not-allowed' : 'pointer',
                  opacity: capturedImages.length >= 10 ? 0.6 : 1,
                  fontSize: '0.8rem',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                Photos
              </button>
            </div>
          </div>
        ) : !isMobile && (
          /* Desktop embedded camera */
          <div style={{
            backgroundColor: 'var(--photo-backdrop)',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '24px',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>
                {capturedImages.length} photos
              </div>
              <button
                onClick={stopCamera}
                type="button"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ position: 'relative', minHeight: '400px' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <button
                type="button"
                onClick={capturePhoto}
                disabled={capturedImages.length >= 10}
                style={{
                  position: 'absolute',
                  bottom: '24px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'var(--bg-card)',
                  border: '4px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '50%',
                  width: '64px',
                  height: '64px',
                  fontSize: '28px',
                  cursor: capturedImages.length >= 10 ? 'not-allowed' : 'pointer',
                  opacity: capturedImages.length >= 10 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                }}
              >
                📷
              </button>
            </div>
          </div>
        )}

        {/* Captured Photos Grid */}
        {capturedImages.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <p style={{
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              marginBottom: '12px',
              fontStyle: 'italic'
            }}>
              Select the image for Google Lens search.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '12px',
              marginBottom: '24px'
            }}>
              {imageUrls.map((url, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedImageIndex(selectedImageIndex === index ? null : index)}
                  style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    overflow: 'hidden',
                    borderRadius: '6px',
                    border: selectedImageIndex === index ? '3px solid var(--primary-light)' : '2px solid var(--border-light)',
                    cursor: 'pointer',
                    transition: 'border 0.2s'
                  }}
                >
                  <img
                    src={url}
                    alt={`Captured ${index + 1}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  {/* Selection indicator checkmark */}
                  {selectedImageIndex === index && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'var(--primary)',
                      color: 'var(--text-inverse)',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      ✓
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(index);
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Search Query Input */}
            <div style={{ marginBottom: '24px' }}>
              <label
                htmlFor="search-query"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)'
                }}
              >
                Optional Search Query:
              </label>
              <textarea
                id="search-query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Add search terms to help refine marketplace search (optional)..."
                disabled={isSearching}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  opacity: isSearching ? 0.6 : 1,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  lineHeight: '1.5'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClearAll}
                disabled={isSearching}
                style={{
                  backgroundColor: 'var(--border-light)',
                  color: 'var(--text-primary)',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: isSearching ? 'not-allowed' : 'pointer',
                  opacity: isSearching ? 0.6 : 1,
                  fontSize: '0.875rem'
                }}
              >
                Clear All
              </button>
              <button
                onClick={runMarketplaceEval}
                disabled={capturedImages.length === 0 || isSearching}
                style={{
                  backgroundColor: 'var(--primary)',
                  color: 'var(--text-inverse)',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: (capturedImages.length === 0 || isSearching) ? 'not-allowed' : 'pointer',
                  opacity: (capturedImages.length === 0 || isSearching) ? 0.6 : 1,
                  fontSize: '0.875rem'
                }}
              >
                {isSearching ? '🔄 Searching Marketplaces...' : '🔍 Search Marketplaces'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MarketplaceEvalTab;
